import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  message,
  Modal,
  Switch,
  Card,
  Avatar,
  Tooltip,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  BankOutlined,
  DollarOutlined,
  ReloadOutlined,
  DownOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { bankAccountsApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { useListReturnState } from '../../hooks/useListReturnState';
import { formatPriceTomanUnitFirst } from '../../utils/formatPriceToman';
import { BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../constants/ui';

const { Option } = Select;

// تبدیل عدد به فارسی
const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const BankAccounts = () => {
  const { canAdd, canEditRecord, canDeleteRecord } = useResourcePermissions('bankaccount');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState(null); // لودینگ برای تغییر وضعیت
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    account_id: 'all',
    account_type: 'all',
    bank_name: 'all',
    search: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    bank_accounts: [],
  });

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [filters, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  const fetchBanks = async () => {
    setLoading(true);
    try {
      const res = await bankAccountsApi.getAll(filters);
      console.log('Banks API Response:', res);
      
      if (res?.banks) {
        setBanks(res.banks);
        setPagination({
          ...pagination,
          total: res.pagination?.total || res.banks.length,
          current: res.pagination?.current_page || 1,
        });
        
        if (res.filters?.bank_accounts) {
          setFilterOptions({
            bank_accounts: res.filters.bank_accounts,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
      message.error('خطا در دریافت لیست حساب‌های بانکی');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, [filters]);

  const handleSearch = (value) => {
    setFilters({ ...filters, search: value });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف حساب بانکی</span>,
      content: <span style={{ fontFamily: 'CustomFont' }}>آیا از حذف این حساب بانکی اطمینان دارید؟ این عملیات قابل بازگشت نیست.</span>,
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        try {
          await bankAccountsApi.delete(id);
          message.success('حساب بانکی با موفقیت حذف شد');
          fetchBanks();
        } catch (error) {
          message.error('خطا در حذف حساب بانکی');
        }
      },
    });
  };

  const handleStatusChange = async (id, checked) => {
    setStatusLoadingId(id);
    try {
      await bankAccountsApi.changeStatus(id, checked ? 1 : 0);
      message.success('وضعیت حساب با موفقیت تغییر کرد');
      fetchBanks();
    } catch (error) {
      message.error('خطا در تغییر وضعیت حساب');
    } finally {
      setStatusLoadingId(null);
    }
  };

  // عملیات گروهی
  const handleBulkAction = async (actionType) => {
    if (selectedRowKeys.length === 0) {
      message.warning('لطفاً حداقل یک حساب را انتخاب کنید');
      return;
    }

    const actionTexts = {
      'delete': 'حذف',
    };

    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>{actionTexts[actionType]} حساب‌های بانکی</span>,
      content: <span style={{ fontFamily: 'CustomFont' }}>آیا از {actionTexts[actionType]} {toPersianNumber(selectedRowKeys.length)} حساب انتخاب شده اطمینان دارید؟</span>,
      okText: 'بله، انجام بده',
      cancelText: 'لغو',
      okButtonProps: { danger: true, style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } },
      cancelButtonProps: { style: { borderColor: '#d9d9d9', color: '#595959' } },
      onOk: async () => {
        setBulkActionLoading(true);
        try {
          await bankAccountsApi.applyQuickAction(actionType, selectedRowKeys);
          message.success(`عملیات ${actionTexts[actionType]} با موفقیت انجام شد`);
          setSelectedRowKeys([]);
          fetchBanks();
        } catch (error) {
          console.error('Error applying bulk action:', error);
          message.error('خطا در انجام عملیات');
        } finally {
          setBulkActionLoading(false);
        }
      },
    });
  };

  // منوی عملیات گروهی
  const bulkActionMenuItems = [
    {
      key: 'delete',
      label: 'حذف حساب‌ها',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleBulkAction('delete'),
    },
  ];

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await bankAccountsApi.exportList(filters);
      message.success('خروجی اکسل در حال دانلود است');
    } catch (error) {
      console.error('Export bank accounts:', error);
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>حساب</span>,
      dataIndex: 'account_name',
      key: 'account_name',
      render: (text, record) => (
        <Space>
          {record.type === 'bank' && record.bank_logo ? (
            <Avatar
              src={record.file_url}
              icon={<BankOutlined />}
              className="bg-blue-100"
            />
          ) : record.type === 'cash' ? (
            <Avatar
              icon={<DollarOutlined />}
              className="bg-green-100 text-green-600"
            />
          ) : (
            <Avatar
              icon={<BankOutlined />}
              className="bg-blue-100 text-blue-600"
            />
          )}
          <div>
            <div className="font-medium" style={{ fontFamily: 'CustomFont' }}>{text}</div>
            {record.bank_name && (
              <div className="text-xs text-gray-500" style={{ fontFamily: 'CustomFont' }}>{record.bank_name}</div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نوع</span>,
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'bank' ? 'blue' : 'green'} style={{ fontFamily: 'CustomFont' }}>
          {type === 'bank' ? 'حساب بانکی' : 'صندوق نقدی'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نوع حساب</span>,
      dataIndex: 'account_type',
      key: 'account_type',
      render: (type) => {
        const types = {
          saving: 'پس‌انداز',
          current: 'جاری',
          'credit-card': 'کارت اعتباری',
          loan: 'وام',
          overdraft: 'اضافه برداشت',
        };
        return type ? <Tag style={{ fontFamily: 'CustomFont' }}>{types[type] || type}</Tag> : '-';
      },
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>موجودی</span>,
      dataIndex: 'bank_balance',
      key: 'bank_balance',
      render: (balance, record) => (
        <span className={balance >= 0 ? 'text-green-600' : 'text-red-600'} style={{ fontFamily: 'CustomFont' }}>
          {formatPriceTomanUnitFirst(balance, { currencySymbol: record.currency_symbol })}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Switch
          checked={status === 1}
          onChange={(checked) => handleStatusChange(record.id, checked)}
          checkedChildren="فعال"
          unCheckedChildren="غیرفعال"
          loading={statusLoadingId === record.id}
        />
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="مشاهده">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigateToDetail(`/finance/bank-accounts/${record.id}`)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            />
          </Tooltip>
          {canEditRecord(record) ? (
            <Tooltip title="ویرایش">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigateToDetail(`/finance/bank-accounts/${record.id}/edit`)}
                className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              />
            </Tooltip>
          ) : null}
          {canDeleteRecord(record) ? (
            <Tooltip title="حذف">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
                className="hover:bg-red-50 dark:hover:bg-red-900/20"
              />
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        {/* Main Container Card */}
        <Card 
          className="shadow-xl border-0"
          styles={{ body: { padding: 0 } }}
        >
          {/* Header Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                  <BankOutlined className="text-emerald-600 dark:text-emerald-400 text-xl" />
                </div>
                <div>
                  <h1 
                    className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    حساب‌های بانکی
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">مدیریت حساب‌های بانکی و صندوق‌های نقدی</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                <Tooltip title="بروزرسانی">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchBanks}
                    size="middle"
                    loading={loading}
                  />
                </Tooltip>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  loading={exporting}
                  size="middle"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  خروجی اکسل
                </Button>
                {canAdd ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/finance/bank-accounts/add')}
                    size="middle"
                    className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    افزودن حساب جدید
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={6} />
            ) : (
              <>
                {/* Filters and Search Bar */}
                <div className="mb-6 flex flex-wrap gap-3 items-center">
                  <Input
                    placeholder="جستجو..."
                    allowClear
                    value={filters.search}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{ fontFamily: 'CustomFont', maxWidth: '250px', width: '100%' }}
                    size="middle"
                    prefix={<SearchOutlined className="text-gray-400" />}
                    className="rounded-lg"
                  />
                  
                  <Select
                    placeholder="نوع"
                    value={filters.type === 'all' ? undefined : filters.type}
                    onChange={(value) => handleFilterChange('type', value || 'all')}
                    style={{ fontFamily: 'CustomFont', minWidth: '130px' }}
                    allowClear
                  >
                    <Option value="bank">حساب بانکی</Option>
                    <Option value="cash">صندوق نقدی</Option>
                  </Select>

                  <Select
                    placeholder="وضعیت"
                    value={filters.status === 'all' ? undefined : filters.status}
                    onChange={(value) => handleFilterChange('status', value || 'all')}
                    style={{ fontFamily: 'CustomFont', minWidth: '130px' }}
                    allowClear
                  >
                    <Option value="1">فعال</Option>
                    <Option value="0">غیرفعال</Option>
                  </Select>

                  <Select
                    placeholder="نوع حساب"
                    value={filters.account_type === 'all' ? undefined : filters.account_type}
                    onChange={(value) => handleFilterChange('account_type', value || 'all')}
                    style={{ fontFamily: 'CustomFont', minWidth: '140px' }}
                    allowClear
                  >
                    <Option value="saving">پس‌انداز</Option>
                    <Option value="current">جاری</Option>
                    <Option value="credit-card">کارت اعتباری</Option>
                    <Option value="loan">وام</Option>
                    <Option value="overdraft">اضافه برداشت</Option>
                  </Select>

                  <Select
                    placeholder="انتخاب حساب"
                    value={filters.account_id === 'all' ? undefined : filters.account_id}
                    onChange={(value) => handleFilterChange('account_id', value || 'all')}
                    style={{ fontFamily: 'CustomFont', minWidth: '180px' }}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                  >
                    {filterOptions.bank_accounts.map((acc) => (
                      <Option key={acc.id} value={acc.id}>
                        {acc.account_name} {acc.bank_name ? `(${acc.bank_name})` : ''}
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* عملیات گروهی */}
                {selectedRowKeys.length > 0 && (
                  <div className={BULK_ACTION_BAR_CLASS}>
                    <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
                      {toPersianNumber(selectedRowKeys.length)} مورد انتخاب شده
                    </span>
                    <Dropdown
                      menu={{ items: bulkActionMenuItems }}
                      trigger={['click']}
                    >
                      <Button
                        type="primary"
                        loading={bulkActionLoading}
                        size="middle"
                        className="border-0"
                        icon={<DownOutlined />}
                        style={{ fontFamily: 'CustomFont', backgroundColor: '#f97316', color: '#fff' }}
                      >
                        عملیات گروهی
                      </Button>
                    </Dropdown>
                  </div>
                )}

                {/* جدول */}
                <Card className="dark:bg-gray-800 border-0 shadow-sm overflow-x-auto">
                  <Table
                    columns={columns}
                    dataSource={banks}
                    rowKey="id"
                    rowSelection={rowSelection}
                    loading={loading}
                    pagination={{
                      ...pagination,
                      showSizeChanger: true,
                      showTotal: (total) => <span style={{ fontFamily: 'CustomFont' }}>مجموع: {toPersianNumber(total)} حساب</span>,
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1100 }}
                  />
                </Card>
              </>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default BankAccounts;
